import ClearIcon from '@mui/icons-material/Clear';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { AppBar, Box, IconButton, InputBase, Toolbar } from '@mui/material';
import logo from 'assets/logo-white-lock.svg';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-v7';
import { UserRole } from 'shared';
import { alertStore } from 'stores/alertStore/alertStore';
import { locationStore } from 'stores/locationStore/locationStore';
import { userStore } from 'stores/userStore/userStore';
import { MenuModal } from './menu/MenuModal';
import { StyledBox, StyledSearchBar } from './StyledComponents';

export default observer(function Navbar() {
	const { t } = useTranslation();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const { locationId } = useParams();

	const handleOpenMenu = () => setIsMenuOpen(true);
	const handleCloseMenu = () => setIsMenuOpen(false);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		alertStore.setSearchPhrase(e.target.value);
	};

	const handleClearSearch = () => alertStore.setSearchPhrase('');

	useEffect(() => {
		if (locationId) locationStore.selectLocation(locationId);
		if (userStore.userContext.userRole === UserRole.LocationAdmin) {
			locationStore.setLocationsCount(1);
		}
	}, []);

	const locationName = locationStore.selectedLocation?.name ?? '';
	const displayName =
		locationStore.locationsCount === 1 ? `tuSprytki - ${locationName}` : locationName;

	return (
		<StyledBox>
			<AppBar position='relative' color='secondary'>
				<Toolbar sx={{ gap: 1 }}>
					<Box sx={{ flexGrow: 0, mr: 1 }}>
						<img alt='logo' src={logo} />
					</Box>
					<Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
						{locationStore.selectedLocation && (
							<Box
								component='span'
								sx={{
									display: 'block',
									overflow: 'hidden',
									whiteSpace: 'nowrap',
									textOverflow: 'ellipsis',
									fontSize: '14px',
									fontWeight: 500,
									color: 'inherit',
								}}
							>
								{displayName}
							</Box>
						)}
					</Box>
					<IconButton
						id='navbar-settings-button'
						size='large'
						edge='end'
						color='inherit'
						aria-label='menu'
						onClick={handleOpenMenu}
					>
						<MenuIcon />
					</IconButton>
				</Toolbar>
			</AppBar>
			<StyledSearchBar>
				<SearchIcon sx={{ color: 'secondary.contrastText', opacity: 0.7 }} />
				<InputBase
					value={alertStore.searchPhrase}
					onChange={handleSearchChange}
					placeholder={t('pages.locations.searchPlaceholder')}
					fullWidth
					sx={{
						color: 'secondary.contrastText',
						'& .MuiInputBase-input': {
							color: 'inherit',
							'&::placeholder': { color: 'inherit', opacity: 0.7 },
						},
					}}
					inputProps={{ 'aria-label': 'search' }}
				/>
				<IconButton
					size='small'
					color='inherit'
					aria-label='clear search'
					onClick={handleClearSearch}
					disabled={!alertStore.searchPhrase}
					sx={{
						color: 'secondary.contrastText',
						'&.Mui-disabled': { color: 'secondary.contrastText', opacity: 0.4 },
					}}
				>
					<ClearIcon fontSize='small' />
				</IconButton>
			</StyledSearchBar>
			<MenuModal isMenuOpen={isMenuOpen} handleCloseMenu={handleCloseMenu} />
		</StyledBox>
	);
});
